import React, { createContext, PropsWithChildren, useEffect, useState, useCallback, useMemo } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import Animated, { useSharedValue, withTiming, useAnimatedStyle, Easing, runOnJS } from "react-native-reanimated";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { HintDefinition, getHint, getHintsForPage } from "@/constants/hints";
import { profileService } from "@/services/profileServices";
import useAuth from "@/hooks/useAuth";

type Rect = { width?: number; height?: number; x?: number; y?: number };

type DismissedHints = Record<string, boolean>;

type HintCtx = {
  // Show a specific hint by ID
  showHint: (hintId: string, location?: Partial<Rect>) => void;
  // Show a hint with custom content
  showCustomHint: (opts: { title: string; message: string; hintId?: string; location?: Partial<Rect> }) => void;
  // Hide the current hint
  hideHint: () => void;
  // Check if a hint has been dismissed
  isHintDismissed: (hintId: string) => boolean;
  // Dismiss a hint (mark as "don't show again")
  dismissHint: (hintId: string) => Promise<void>;
  // Reset a dismissed hint (allow it to show again)
  resetHint: (hintId: string) => Promise<void>;
  // Get all hints for a page that haven't been dismissed
  getActiveHintsForPage: (page: string) => HintDefinition[];
  // Whether the hint panel is visible
  visible: boolean;
  // Loading state for fetching/saving dismissed hints
  loading: boolean;
};

export const HintContext = createContext<HintCtx | undefined>(undefined);

export function HintProvider({ children }: PropsWithChildren) {
  const { colors } = useTheme();
  const { user } = useAuth();

  const [visible, setVisible] = useState(false);
  const [currentHint, setCurrentHint] = useState<{ title: string; message: string; hintId?: string } | null>(null);
  const [dismissedHints, setDismissedHints] = useState<DismissedHints>({});
  const [loading, setLoading] = useState(true);

  const [location, setLocation] = useState<Required<Rect>>({
    width: 320,
    height: 150,
    x: 100,
    y: 100,
  });

  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.95);

  const animateIn = () => {
    opacity.value = withTiming(1, { duration: 250, easing: Easing.out(Easing.cubic) });
    scale.value = withTiming(1, { duration: 250, easing: Easing.out(Easing.cubic) });
  };

  const animateOut = (after?: () => void) => {
    opacity.value = withTiming(0, { duration: 200, easing: Easing.in(Easing.cubic) }, (finished) => {
      if (finished && after) runOnJS(after)();
    });
    scale.value = withTiming(0.95, { duration: 200, easing: Easing.in(Easing.cubic) });
  };

  // Fetch dismissed hints from user profile on mount
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    const fetchDismissedHints = async () => {
      try {
        const dismissed = await profileService.getDismissedHints(user.id);
        setDismissedHints(dismissed);
      } catch (error) {
        console.error("Error fetching dismissed hints:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDismissedHints();
  }, [user?.id]);

  useEffect(() => {
    if (visible) animateIn();
    else
      animateOut(() => {
        setCurrentHint(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const aStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  const showHint = useCallback(
    (hintId: string, loc?: Partial<Rect>) => {
      // Don't show if already dismissed
      if (dismissedHints[hintId]) return;

      const hint = getHint(hintId);
      if (!hint) return;

      if (loc) {
        setLocation((prev) => ({
          width: loc.width ?? prev.width,
          height: loc.height ?? prev.height,
          x: loc.x ?? prev.x,
          y: loc.y ?? prev.y,
        }));
      }

      setCurrentHint({ title: hint.title, message: hint.message, hintId: hint.id });
      setVisible(true);
    },
    [dismissedHints],
  );

  const showCustomHint = useCallback(
    (opts: { title: string; message: string; hintId?: string; location?: Partial<Rect> }) => {
      // Don't show if hintId provided and already dismissed
      if (opts.hintId && dismissedHints[opts.hintId]) return;

      if (opts.location) {
        setLocation((prev) => ({
          width: opts.location?.width ?? prev.width,
          height: opts.location?.height ?? prev.height,
          x: opts.location?.x ?? prev.x,
          y: opts.location?.y ?? prev.y,
        }));
      }

      setCurrentHint({ title: opts.title, message: opts.message, hintId: opts.hintId });
      setVisible(true);
    },
    [dismissedHints],
  );

  const hideHint = useCallback(() => {
    setVisible(false);
  }, []);

  const isHintDismissed = useCallback(
    (hintId: string) => {
      return !!dismissedHints[hintId];
    },
    [dismissedHints],
  );

  const dismissHint = useCallback(
    async (hintId: string) => {
      if (!user?.id) return;

      // Optimistically update local state
      setDismissedHints((prev) => ({ ...prev, [hintId]: true }));

      try {
        await profileService.dismissHint(user.id, hintId);
      } catch (error) {
        // Revert on error
        console.error("Error dismissing hint:", error);
        setDismissedHints((prev) => {
          const next = { ...prev };
          delete next[hintId];
          return next;
        });
      }
    },
    [user?.id],
  );

  const resetHint = useCallback(
    async (hintId: string) => {
      if (!user?.id) return;

      // Optimistically update local state
      setDismissedHints((prev) => {
        const next = { ...prev };
        delete next[hintId];
        return next;
      });

      try {
        await profileService.resetHint(user.id, hintId);
      } catch (error) {
        // Revert on error
        console.error("Error resetting hint:", error);
        setDismissedHints((prev) => ({ ...prev, [hintId]: true }));
      }
    },
    [user?.id],
  );

  const getActiveHintsForPage = useCallback(
    (page: string) => {
      return getHintsForPage(page).filter((hint) => !dismissedHints[hint.id]);
    },
    [dismissedHints],
  );

  const handleDismissForever = useCallback(async () => {
    if (currentHint?.hintId) {
      await dismissHint(currentHint.hintId);
    }
    hideHint();
  }, [currentHint?.hintId, dismissHint, hideHint]);

  const contextValue = useMemo(
    () => ({
      showHint,
      showCustomHint,
      hideHint,
      isHintDismissed,
      dismissHint,
      resetHint,
      getActiveHintsForPage,
      visible,
      loading,
    }),
    [
      showHint,
      showCustomHint,
      hideHint,
      isHintDismissed,
      dismissHint,
      resetHint,
      getActiveHintsForPage,
      visible,
      loading,
    ],
  );

  return (
    <HintContext.Provider value={contextValue}>
      {children}

      {/* Hint overlay */}
      <Animated.View
        pointerEvents={visible ? "auto" : "none"}
        style={[
          styles.container,
          {
            left: location.x,
            top: location.y,
          },
          aStyle,
        ]}
      >
        {currentHint && (
          <View style={[styles.hintCard, { backgroundColor: colors.background, borderColor: colors.border }]}>
            <View style={styles.header}>
              <ThemedText type="subtitle" style={styles.title}>
                {currentHint.title}
              </ThemedText>
              <Button icon="close" style={styles.closeButton} onPress={hideHint} />
            </View>
            <ThemedText style={styles.message}>{currentHint.message}</ThemedText>
            <View style={styles.footer}>
              {currentHint.hintId && (
                <Pressable onPress={handleDismissForever} style={styles.dontShowAgain}>
                  <ThemedText type="small" style={{ color: colors.textSecondary }}>
                    Don&apos;t show again
                  </ThemedText>
                </Pressable>
              )}
              <Button
                text="Got it"
                style={[styles.gotItButton, { backgroundColor: colors.primary }]}
                textStyle={{ color: "white" }}
                onPress={hideHint}
              />
            </View>
          </View>
        )}
      </Animated.View>
    </HintContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    zIndex: 1000,
    elevation: 1000,
  },
  hintCard: {
    width: 320,
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  title: {
    flex: 1,
    fontWeight: "600",
  },
  closeButton: {
    width: 28,
    height: 28,
    marginTop: -4,
    marginRight: -4,
  },
  message: {
    lineHeight: 20,
    marginBottom: 16,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dontShowAgain: {
    paddingVertical: 8,
  },
  gotItButton: {
    paddingHorizontal: 16,
    height: 32,
  },
});
